import { FormEvent, FunctionComponent, useCallback, useMemo, useState, useEffect, MouseEventHandler } from 'react'
import { observer } from 'mobx-react-lite'
import { makeStyles } from '@griffel/react'
import { RadioGroup, Radio, RadioGroupOnChangeData } from '@fluentui/react-components'
import { Field } from '@fluentui/react-field'
import { Button } from '@fluentui/react-button'
import { DismissRegular } from '@fluentui/react-icons'
import { tokens } from '@fluentui/tokens'
import { IFilter, useGenericListSettings, getParamId } from '@knowmax/genericlist-core'
import { ToggleOpenButton } from '../core'

const useClasses = makeStyles({
  head: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    cursor: 'pointer',    
    ':hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover
    }
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacingVerticalM
  }
})

export const FilterOptions: FunctionComponent<{ filter: IFilter, listId?: string }> = observer(({ filter, listId }) => {
  const [open, setOpen] = useState(true)
  const settings = useGenericListSettings()
  const classes = useClasses()

  const filterparamid = useMemo(() => getParamId(filter.id, listId), [filter.id, listId])

  useEffect(() => {
    if (filter.collapsed === true && !settings.params.get(filterparamid)) {
      setOpen(false)
    }
  }, [filter.collapsed, filterparamid, settings.params])

  const toggleopen = useCallback(() => {
    setOpen(!open)
  }, [open])

  const selected = useMemo(() => {
    const id = settings.params.get(filterparamid)

    if (id) {
      return id
    } else {
      // get default option
      return filter.options?.find(o => o.value === undefined && o.expression === undefined)?.id
    }
  }, [filter, filterparamid, settings.params])


  const select = useCallback((ev: FormEvent<HTMLDivElement>, data: RadioGroupOnChangeData) => {
    const params = new URLSearchParams(settings.params)
    params.delete(filterparamid)

    if (data.value) {
      const o = filter.options?.find(o => o.id === data.value)

      if (o && ((o.expression !== undefined && o.expression !== '') || (o.value !== undefined))) {
        params.set(filterparamid, o.id)
      }

      settings.serialize(params)
    }
  }, [filter, filterparamid, settings.params])

  const clear = useCallback<MouseEventHandler>((event) => {
    event.stopPropagation()

    const params = new URLSearchParams(settings.params)

    if (params.has(filterparamid)) {
      params.delete(filterparamid)
      settings.serialize(params)
    }
  }, [filterparamid, settings.params])

  return (
    <Field label={
      <div className={classes.head} onClick={toggleopen}>
        {filter.name}

        <div className={classes.actions}>
          {selected !== undefined &&
            <Button
              onClick={clear}
              appearance='transparent'
              icon={<DismissRegular />}
            />
          }

          <ToggleOpenButton value={open} />
        </div>
      </div>}>

      {open && filter.options &&
        <RadioGroup value={selected ?? ''} onChange={select} >
          {filter.options.map((option) =>
            <Radio key={option.id} value={option.id} label={option.name ?? option.id} />)}
        </RadioGroup>
      }
    </Field>
  )
})